import { useTranslation } from 'react-i18next'
import { isSuperAdmin } from '@/utils/permissions'

interface NewProjectModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateProject: () => void
  user: any
  // Form state
  newProjectName: string
  onProjectNameChange: (name: string) => void
  selectedAgencyId: string
  onAgencyIdChange: (id: string) => void
  agencies: any[]
}

export const NewProjectModal = ({
  isOpen,
  onClose,
  onCreateProject,
  user,
  newProjectName,
  onProjectNameChange,
  selectedAgencyId,
  onAgencyIdChange,
  agencies,
}: NewProjectModalProps) => {
  const { t } = useTranslation(['dashboard', 'common'])

  if (!isOpen) return null

  const isDisabled =
    !newProjectName.trim() ||
    (isSuperAdmin(user) && !selectedAgencyId)

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-framed-neutral-800">{t('dashboard:newProjectModal.title')}</h3>
          <p className="text-sm text-framed-neutral-500 mt-1">{t('dashboard:newProjectModal.subtitle')}</p>
        </div>

        {/* Agency Selection (Super Admin Only) */}
        {isSuperAdmin(user) && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-framed-neutral-700 mb-2">{t('dashboard:newProjectModal.agencyLabel')}</label>
            <select
              value={selectedAgencyId}
              onChange={(e) => onAgencyIdChange(e.target.value)}
              className="w-full h-11 px-4 bg-white border border-framed-neutral-300 rounded-xl text-framed-neutral-800 focus:outline-none focus:ring-2 focus:ring-framed-yellow/40 focus:border-transparent"
            >
              <option value="">{t('dashboard:newProjectModal.agencyPlaceholder')}</option>
              {agencies.map((ag) => (
                <option key={ag.id} value={ag.id}>{ag.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Project Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-framed-neutral-700 mb-2">{t('dashboard:newProjectModal.projectNameLabel')}</label>
          <input
            type="text"
            value={newProjectName}
            onChange={(e) => onProjectNameChange(e.target.value)}
            className="w-full h-11 px-4 bg-white border border-framed-neutral-300 rounded-xl text-framed-neutral-800 placeholder-framed-neutral-400 focus:outline-none focus:ring-2 focus:ring-framed-yellow-400 focus:border-transparent"
            placeholder={t('dashboard:newProjectModal.namePlaceholder')}
            autoFocus
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-framed-neutral-300">
          <button
            onClick={onClose}
            className="flex-1 h-11 px-4 font-medium text-framed-neutral-600 bg-framed-neutral-100 hover:bg-framed-neutral-200 rounded-xl transition-colors"
          >
            {t('dashboard:newProjectModal.cancel')}
          </button>
          <button
            onClick={onCreateProject}
            disabled={isDisabled}
            className="flex-1 h-11 px-4 font-medium text-white bg-framed-neutral-800 hover:bg-framed-neutral-900 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('dashboard:newProjectModal.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
